import React, { useEffect, useState } from 'react';
import jwtService from '../services/jwtService';

interface LogEntry {
  id: string;
  timestamp: string;
  source?: string;
  event?: string;
  transactionId?: string | null;
  body?: any;
  headers?: any;
  status?: string;
  service?: string;
  endpoint?: string;
  method?: string;
  request?: any;
  response?: any;
  statusCode?: number | null;
}

const ApiLogPage: React.FC = () => {
  const [webhooks, setWebhooks] = useState<LogEntry[]>([]);
  const [apis, setApis] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const authHeader = jwtService.getAdminAuthHeader();
      const headers: HeadersInit = authHeader ? { 'Authorization': authHeader } : {};
      
      const [whRes, apiRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/logs/webhooks?limit=100`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/logs/apis?limit=100`, { headers })
      ]);
      const whJson = await whRes.json();
      const apiJson = await apiRes.json();
      if (whJson.success) setWebhooks(whJson.data);
      if (apiJson.success) setApis(apiJson.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // Subscribe to SSE for real-time logs in browser devtools/Network
    const evt = new EventSource(`${process.env.REACT_APP_API_URL}/logs/stream`);
    evt.addEventListener('webhook', (e: MessageEvent) => {
      try {
        const log = JSON.parse(e.data) as LogEntry;
        setWebhooks(prev => [log, ...prev].slice(0, 100));
      } catch {}
    });
    evt.addEventListener('api', (e: MessageEvent) => {
      try {
        const log = JSON.parse(e.data) as LogEntry;
        setApis(prev => [log, ...prev].slice(0, 100));
      } catch {}
    });
    evt.addEventListener('error', () => {
      // no-op
    });
    return () => evt.close();
  }, []);

  const clearLogs = async () => {
    await fetch(`${process.env.REACT_APP_API_URL}/logs/clear`, { method: 'DELETE' });
    fetchLogs();
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">API & Webhook Logs</h1>
          <div className="space-x-3">
            <button onClick={fetchLogs} className="px-4 py-2 bg-blue-600 rounded hover:bg-blue-700">Refresh</button>
            <button onClick={clearLogs} className="px-4 py-2 bg-red-600 rounded hover:bg-red-700">Clear</button>
          </div>
        </div>

        {loading && <div className="mb-4 text-blue-300">Loading...</div>}
        {error && <div className="mb-4 text-red-400">{error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
            <h2 className="text-lg font-semibold mb-3">Webhook Logs</h2>
            <div className="space-y-3 max-h-[70vh] overflow-auto">
              {webhooks.length === 0 && <div className="text-gray-400">No webhook logs</div>}
              {webhooks.map((log) => (
                <div key={log.id} className="bg-gray-800/60 rounded p-3">
                  <div className="text-xs text-gray-400">{new Date(log.timestamp).toLocaleString()}</div>
                  <div className="text-sm"><span className="text-gray-400">Source:</span> {log.source}</div>
                  <div className="text-sm"><span className="text-gray-400">Event:</span> {log.event}</div>
                  <div className="text-sm"><span className="text-gray-400">Status:</span> {log.status}</div>
                  {log.transactionId && (
                    <div className="text-sm"><span className="text-gray-400">Transaction:</span> {log.transactionId}</div>
                  )}
                  <details className="mt-2">
                    <summary className="cursor-pointer text-gray-300">Payload</summary>
                    <pre className="text-xs whitespace-pre-wrap break-all bg-gray-900 p-2 rounded mt-2">{JSON.stringify(log.body, null, 2)}</pre>
                  </details>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
            <h2 className="text-lg font-semibold mb-3">API Logs</h2>
            <div className="space-y-3 max-h-[70vh] overflow-auto">
              {apis.length === 0 && <div className="text-gray-400">No API logs</div>}
              {apis.map((log) => (
                <div key={log.id} className="bg-gray-800/60 rounded p-3">
                  <div className="text-xs text-gray-400">{new Date(log.timestamp).toLocaleString()}</div>
                  <div className="text-sm"><span className="text-gray-400">Endpoint:</span> {log.method} {log.endpoint}</div>
                  <div className="text-sm"><span className="text-gray-400">Status:</span> {log.statusCode}</div>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-gray-300">Request</summary>
                    <pre className="text-xs whitespace-pre-wrap break-all bg-gray-900 p-2 rounded mt-2">{JSON.stringify(log.request, null, 2)}</pre>
                  </details>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-gray-300">Response</summary>
                    <pre className="text-xs whitespace-pre-wrap break-all bg-gray-900 p-2 rounded mt-2">{JSON.stringify(log.response, null, 2)}</pre>
                  </details>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiLogPage;


